// =============================================
// DataForSEO Service — keyword data, SERP, PAA
// v3.0: Cache in Supabase, rate limiting, batch processing
// v3.1 (2026-05-26): Migracja na server-side proxy /api/dataforseo (Faza 3
// per-workspace API keys). Basic Auth wstrzykiwane przez nginx z env vars
// DATAFORSEO_BASIC_AUTH_FIRMA / _SPONSORAKI (per X-Workspace header).
// Klucze VITE_DATAFORSEO_LOGIN/PASSWORD wypadly z bundle — zero secrets in JS.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logDebug, logError } from "./contentEngine/contentLogger";
import { getActiveWorkspace } from "@/lib/workspace";

// Relative URL — frontend wola lokalny nginx proxy, ktory:
//   1. czyta naglowek X-Workspace
//   2. wstrzykuje Authorization: Basic z DATAFORSEO_BASIC_AUTH_{FIRMA|SPONSORAKI}
//   3. forwarduje do https://api.dataforseo.com/v3/*
const API_BASE = "/api/dataforseo/v3";
const MAX_BATCH_SIZE = 100;
const CACHE_DURATION_DAYS = 20;

// Rate limiting
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL_MS = 200; // 5 req/s

async function rateLimitedFetch(url: string, options: RequestInit): Promise<Response> {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL_MS) {
    await new Promise((resolve) =>
      setTimeout(resolve, MIN_REQUEST_INTERVAL_MS - timeSinceLastRequest),
    );
  }
  lastRequestTime = Date.now();
  return fetch(url, options);
}

// =============================================
// Cache layer
// =============================================

// Track if cache table is available (avoid spamming 406 errors)
let cacheAvailable: boolean | null = null;

async function getCached(
  query: string,
  queryType: string,
  locationCode = 2616,
  languageCode = "pl",
): Promise<Record<string, unknown> | null> {
  // Skip cache if we already know the table doesn't exist
  if (cacheAvailable === false) return null;

  try {
    const { data, error } = await (supabase as any)
      .from("dataforseo_cache")
      .select("data, expires_at")
      .eq("query", query)
      .eq("query_type", queryType)
      .eq("location_code", locationCode)
      .eq("language_code", languageCode)
      .maybeSingle();

    if (error) {
      // Table doesn't exist or RLS blocks — disable cache for this session
      logDebug("DataForSEO", `Cache niedostępny: ${error.message}`);
      cacheAvailable = false;
      return null;
    }

    cacheAvailable = true;

    if (data && new Date(data.expires_at) > new Date()) {
      return data.data as Record<string, unknown>;
    }
  } catch {
    cacheAvailable = false;
  }
  return null;
}

async function setCache(
  query: string,
  queryType: string,
  responseData: Record<string, unknown>,
  locationCode = 2616,
  languageCode = "pl",
): Promise<void> {
  // Skip if cache table not available
  if (cacheAvailable === false) return;

  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + CACHE_DURATION_DAYS);

    await (supabase as any).from("dataforseo_cache").upsert(
      {
        query,
        query_type: queryType,
        location_code: locationCode,
        language_code: languageCode,
        data: responseData,
        fetched_at: new Date().toISOString(),
        expires_at: expiresAt.toISOString(),
      },
      { onConflict: "query,query_type,location_code,language_code" },
    );
  } catch {
    cacheAvailable = false;
  }
}

// =============================================
// DataForSEO API calls
// =============================================

/**
 * Sanitize keyword for DataForSEO API.
 * API requirement: "Special characters and UTF symbols are not allowed"
 * - Strip Polish diacritics (ą→a, ć→c, etc.)
 * - Remove question marks and special punctuation
 * - Lowercase, trim, max 80 chars
 */
/**
 * Sanitize keyword for DataForSEO API.
 * Rules from docs:
 * - "Special characters and UTF symbols are not allowed"
 * - Max 80 characters, max 10 words per keyword
 * Polish diacritics → ASCII, strip punctuation, trim to 10 words.
 */
export function sanitizeKeyword(keyword: string): string {
  const PL_MAP: Record<string, string> = {
    "ą": "a", "ć": "c", "ę": "e", "ł": "l", "ń": "n",
    "ó": "o", "ś": "s", "ź": "z", "ż": "z",
    "Ą": "A", "Ć": "C", "Ę": "E", "Ł": "L", "Ń": "N",
    "Ó": "O", "Ś": "S", "Ź": "Z", "Ż": "Z",
  };

  // Polish stop words to remove (filler words that waste the 10-word limit)
  const STOP_WORDS = new Set([
    "i", "a", "w", "z", "na", "do", "od", "o", "czy", "co", "to",
    "jest", "sie", "nie", "jak", "dla", "po", "ze", "za", "przy",
    "lub", "albo", "oraz", "tego", "tej", "ten", "ta", "te",
  ]);

  let cleaned = keyword
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (ch) => PL_MAP[ch] || ch)
    .replace(/[?!.,;:'"„""«»()[\]{}\-–—]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  // If > 10 words, remove stop words first, then truncate
  let words = cleaned.split(" ").filter(Boolean);
  if (words.length > 10) {
    words = words.filter((w) => !STOP_WORDS.has(w));
  }
  // Still > 10? Take first 10
  if (words.length > 10) {
    words = words.slice(0, 10);
  }

  return words.join(" ").slice(0, 80);
}

async function callDataForSEO(
  endpoint: string,
  payload: unknown[],
): Promise<Record<string, unknown>> {
  logDebug("DataForSEO", `POST ${endpoint}`, { payload: JSON.stringify(payload).slice(0, 300) });

  const response = await rateLimitedFetch(`${API_BASE}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // Authorization NIE jest wysylane z frontu — nginx /api/dataforseo/
      // wstrzykuje Basic auth z env DATAFORSEO_BASIC_AUTH_{FIRMA|SPONSORAKI}.
      "X-Workspace": getActiveWorkspace(),
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    logError("DataForSEO", `Error ${response.status}: ${JSON.stringify(result).slice(0, 500)}`);
    throw new Error(`DataForSEO API error: ${response.status} — ${(result as any)?.tasks?.[0]?.status_message || "Unknown"}`);
  }

  // Log task status for debugging
  const tasks = (result as any)?.tasks || [];
  for (const task of tasks) {
    if (task.status_code !== 20000) {
      logDebug("DataForSEO", `Task warning: ${task.status_code} — ${task.status_message}`);
    }
  }

  return result;
}

// =============================================
// Public API
// =============================================

export interface KeywordDataResult {
  keyword: string;
  search_volume: number;
  cpc: number;
  competition: number;
  keyword_difficulty: number;
  monthly_searches?: Array<{ month: string; search_volume: number }>;
}

/**
 * Get keyword data (volume, CPC, KD) for a list of keywords.
 * Uses cache first, then fetches from DataForSEO.
 */
export async function getKeywordData(
  keywords: string[],
  locationCode = 2616,
  languageCode = "pl",
): Promise<KeywordDataResult[]> {
  const results: KeywordDataResult[] = [];
  const uncached: string[] = [];

  // Check cache first
  for (const keyword of keywords) {
    const cached = await getCached(keyword, "keyword_data", locationCode, languageCode);
    if (cached) {
      results.push(cached as unknown as KeywordDataResult);
    } else {
      uncached.push(keyword);
    }
  }

  if (uncached.length === 0) return results;

  // Sanitize keywords for DataForSEO (strip Polish diacritics, special chars)
  const sanitizedMap = new Map<string, string>(); // sanitized → original
  const sanitizedBatch: string[] = [];
  for (const kw of uncached) {
    const sanitized = sanitizeKeyword(kw);
    if (sanitized.length > 0) {
      sanitizedMap.set(sanitized, kw);
      sanitizedBatch.push(sanitized);
    }
  }

  if (sanitizedBatch.length === 0) return results;

  // Batch fetch uncached keywords
  const batches: string[][] = [];
  for (let i = 0; i < sanitizedBatch.length; i += MAX_BATCH_SIZE) {
    batches.push(sanitizedBatch.slice(i, i + MAX_BATCH_SIZE));
  }

  for (const batch of batches) {
    // DataForSEO search_volume/live: ONE task with array of keywords
    const payload = [
      {
        keywords: batch,
        location_code: locationCode,
        language_code: languageCode,
      },
    ];

    try {
      const response = await callDataForSEO(
        "/keywords_data/google_ads/search_volume/live",
        payload,
      );

      const tasks = (response as any)?.tasks || [];
      for (const task of tasks) {
        const items = task?.result || [];
        for (const item of items) {
          // Map back to original keyword (with Polish chars)
          const originalKeyword = sanitizedMap.get(item.keyword) || item.keyword || "";
          const kwData: KeywordDataResult = {
            keyword: originalKeyword,
            search_volume: item.search_volume || 0,
            cpc: item.cpc || 0,
            competition: item.competition || 0,
            keyword_difficulty: item.keyword_info?.keyword_difficulty || 0,
            monthly_searches: item.monthly_searches?.map(
              (m: { month: string; search_volume: number }) => ({
                month: m.month,
                search_volume: m.search_volume,
              }),
            ),
          };

          results.push(kwData);

          // Cache with original keyword (Polish chars) so next lookup works
          await setCache(
            originalKeyword,
            "keyword_data",
            kwData as unknown as Record<string, unknown>,
            locationCode,
            languageCode,
          );
        }
      }
    } catch (error) {
      logError("DataForSEO", "keyword data error", error);
      // Return partial results
    }
  }

  return results;
}

/**
 * Get SERP (organic search results) for a query.
 */
export async function getOrganicResults(
  keyword: string,
  locationCode = 2616,
  languageCode = "pl",
  depth = 10,
): Promise<unknown[]> {
  const cacheKey = `${keyword}_depth${depth}`;
  const cached = await getCached(cacheKey, "serp_organic", locationCode, languageCode);
  if (cached) return (cached as any).results || [];

  const payload = [
    {
      keyword,
      location_code: locationCode,
      language_code: languageCode,
      depth,
    },
  ];

  const response = await callDataForSEO("/serp/google/organic/live/regular", payload);

  const items = (response as any)?.tasks?.[0]?.result?.[0]?.items || [];
  await setCache(cacheKey, "serp_organic", { results: items }, locationCode, languageCode);
  return items;
}

/**
 * Get People Also Ask questions for a query.
 * Uses /advanced endpoint with people_also_ask_click_depth for PAA expansion.
 */
export async function getPeopleAlsoAsk(
  keyword: string,
  locationCode = 2616,
  languageCode = "pl",
): Promise<string[]> {
  // Brak frontend-side configuration check — klucze server-side w nginx
  // (DATAFORSEO_BASIC_AUTH_{FIRMA|SPONSORAKI}). Gdy puste → upstream 401
  // → logError + return [] (graceful degradation w catch).
  const sanitized = sanitizeKeyword(keyword);
  const cached = await getCached(sanitized, "paa", locationCode, languageCode);
  if (cached) {
    const cachedQuestions = (cached as any).questions || [];
    logDebug("DataForSEO", `PAA cache hit: keyword="${sanitized}", questions=${cachedQuestions.length}`);
    return cachedQuestions;
  }

  const payload = [
    {
      keyword: sanitized,
      location_code: locationCode,
      language_code: languageCode,
      device: "desktop",
      people_also_ask_click_depth: 2,
    },
  ];

  try {
    logDebug("DataForSEO", `PAA fetching: endpoint=/serp/google/organic/live/advanced, keyword="${sanitized}", click_depth=2`);
    const response = await callDataForSEO("/serp/google/organic/live/advanced", payload);
    const items = (response as any)?.tasks?.[0]?.result?.[0]?.items || [];
    const paaItems = items.filter((item: any) => item.type === "people_also_ask");
    const questions = paaItems.flatMap((item: any) =>
      (item.items || []).map((q: any) => q.title || q.question || ""),
    ).filter(Boolean);

    // Deduplicate
    const unique = [...new Set(questions as string[])].slice(0, 20);

    logPublic("DataForSEO", `PAA result: questions=${unique.length}${unique.length > 0 ? `, first="${unique[0]}"` : ""}`);

    await setCache(sanitized, "paa", { questions: unique }, locationCode, languageCode);
    return unique;
  } catch (err) {
    logError("DataForSEO", `PAA error for "${sanitized}"`, err);
    return [];
  }
}

/**
 * Get related keywords for a seed keyword.
 */
export async function getRelatedKeywords(
  keyword: string,
  locationCode = 2616,
  languageCode = "pl",
  limit = 50,
): Promise<KeywordDataResult[]> {
  const cached = await getCached(keyword, "related", locationCode, languageCode);
  if (cached) return (cached as any).keywords || [];

  const sanitized = sanitizeKeyword(keyword);
  const payload = [
    {
      keywords: [sanitized],
      location_code: locationCode,
      language_code: languageCode,
      limit,
    },
  ];

  try {
    const response = await callDataForSEO(
      "/keywords_data/google_ads/keywords_for_keywords/live",
      payload,
    );

    const items = (response as any)?.tasks?.[0]?.result || [];
    const keywords: KeywordDataResult[] = items.map((item: any) => ({
      keyword: item.keyword || "",
      search_volume: item.search_volume || 0,
      cpc: item.cpc || 0,
      competition: item.competition || 0,
      keyword_difficulty: item.keyword_info?.keyword_difficulty || 0,
    }));

    await setCache(keyword, "related", { keywords }, locationCode, languageCode);
    return keywords;
  } catch {
    return [];
  }
}

/**
 * Get ranked keywords for a URL — what keywords does this URL rank for in Google.
 * Uses DataForSEO Labs: /v3/dataforseo_labs/google/ranked_keywords/live
 */
export async function getRankedKeywords(
  targetUrl: string,
  locationName = "Poland",
  languageName = "Polish",
  limit = 10,
): Promise<Array<{ keyword: string; search_volume: number; position: number }>> {
  const cacheKey = `ranked_${targetUrl}`;
  const cached = await getCached(cacheKey, "ranked_keywords", 2616, "pl");
  if (cached) return (cached as any).keywords || [];

  const payload = [
    {
      target: targetUrl,
      location_name: locationName,
      language_name: languageName,
      limit,
      load_rank_absolute: true,
      filters: [
        ["keyword_data.keyword_info.search_volume", ">", 0],
      ],
    },
  ];

  try {
    const response = await callDataForSEO(
      "/dataforseo_labs/google/ranked_keywords/live",
      payload,
    );

    const items = (response as any)?.tasks?.[0]?.result?.[0]?.items || [];
    const keywords = items.map((item: any) => ({
      keyword: item?.keyword_data?.keyword || "",
      search_volume: item?.keyword_data?.keyword_info?.search_volume || 0,
      position: item?.ranked_serp_element?.serp_item?.rank_absolute || 0,
    })).filter((k: any) => k.keyword);

    await setCache(cacheKey, "ranked_keywords", { keywords }, 2616, "pl");
    return keywords;
  } catch (err) {
    logError("DataForSEO", `getRankedKeywords failed for ${targetUrl}`, err);
    return [];
  }
}

/**
 * Stub — server-side keys (nginx-injected) NIE da sie sprawdzic z frontu.
 * Zachowane jako stub (zawsze true) dla backward compat z callers.
 * Faktyczna konfiguracja = env DATAFORSEO_LOGIN_{FIRMA|SPONSORAKI} w Coolify.
 * Brak konfiguracji → upstream 401 → graceful return [] w catch path.
 */
export function isDataForSEOConfigured(): boolean {
  return true;
}
